import { useCallback, useEffect, useState } from 'react';
import settingsService from '@/shared/services/settingsService';

/**
 * The university's student-employment rules, for pages that quote a number
 * from them. `rules` is null until the server answers, and stays null if the
 * request fails (`error` is then true; `reload` tries again). With `skip` no
 * request is made.
 *
 * @returns {{ rules: object | null, loading: boolean, error: boolean,
 *   reload: () => void }}
 */
export default function useStudentEmploymentRules({ skip = false } = {}) {
    const [rules, setRules] = useState(null);
    const [loading, setLoading] = useState(!skip);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (skip) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(false);
        settingsService
            .getStudentEmploymentRules()
            .then((data) => {
                if (cancelled) return;
                setRules(data);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setError(true);
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [skip, attempt]);

    const reload = useCallback(() => setAttempt((n) => n + 1), []);

    return { rules, loading, error, reload };
}
